/**
 * Loads the authenticated user BEFORE permission-gated middleware and the router plugin
 * run, and turns an invalid session into a login redirect rather than an access-denied.
 *
 * @nuxt-alt/auth used to populate `auth.user` during plugin init, so it was always present
 * by the time middleware / router.beforeEach ran. Our useAuth() loads it lazily, so without
 * this global middleware the (synchronous) access.ts would see a null user on a fresh
 * load/reload and wrongly redirect. Global middleware run before named middleware. Guarded
 * to fetch only once (useUserStore().me() is a no-op when the user is already loaded).
 *
 * `loggedIn` only means "a token cookie exists". If a token is present but /me can't load a
 * user (stale/revoked token, refresh failed), the session is invalid — that's an
 * authentication failure, not a permission failure — so we clear it and send the user
 * through the login flow instead of letting access.ts deny them.
 */
export default defineNuxtRouteMiddleware(async to => {
  if (import.meta.server) return;

  // The login/logout pages manage auth state themselves.
  if (to.path.startsWith('/login') || to.path.startsWith('/logout')) return;

  const auth = useAuth();
  if (!auth.loggedIn || auth.user) return;

  try {
    await useUserStore().me();
  } catch {
    // fall through to the invalid-session handling below
  }

  if (!auth.user) {
    await auth.logout();
    return navigateTo('/login');
  }
});
